import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { petAPI, clinicAPI, outpatientAPI, authUtils } from "../../services/api";
import { showErrorToast, showSuccessToast } from "../../utils/notificationUtils";
import { useClinicData } from "../../hooks/useClinicData";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import SelectPaymentModal from "./SelectPaymentModal";
import { useTranslation } from "react-i18next";

const EditOutpatientModal = ({
  isOpen,
  onClose,
  onSave,
  vets = [],
  workingHours = [],
  appointmentTypes = [],
  editData = null, // The outpatient visit data to edit
}) => {
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "ar" ? "ar-EG" : "en-US";
  const fallbackLabels = useMemo(
    () => ({
      petGuardian: t("receptionOutpatients.tab.fallback.petGuardian"),
      pet: t("receptionOutpatients.tab.fallback.pet"),
      vet: t("receptionOutpatients.tab.fallback.vet"),
    }),
    [t]
  );
  const getVetDisplayName = useCallback(
    (vet) => {
      if (!vet) return "";
      return (
        vet.user?.name ||
        vet.name ||
        t("receptionOutpatients.admit.fields.vet.generic", {
          id: vet.user_id,
        })
      );
    },
    [t]
  );

  // Get clinic data using the hook
  const { getClinicId } = useClinicData();
  const [petOwnerSearch, setPetOwnerSearch] = useState("");
  const [selectedPetOwner, setSelectedPetOwner] = useState(null);
  const [petOwnerPets, setPetOwnerPets] = useState([]);
  const [petOwnerSearchResults, setPetOwnerSearchResults] = useState([]);
  const [showPetOwnerDropdown, setShowPetOwnerDropdown] = useState(false);
  const [isSearchingPetOwners, setIsSearchingPetOwners] = useState(false);
  const [vetSearch, setVetSearch] = useState("");
  const [selectedVet, setSelectedVet] = useState(null);
  const [showVetDropdown, setShowVetDropdown] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [downpayment, setDownpayment] = useState(null);
  const [originalDownpayment, setOriginalDownpayment] = useState(null);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paymentNotes, setPaymentNotes] = useState("");
  const [editOutpatient, setEditOutpatient] = useState({
    id: null,
    pet_owner: "",
    pet: "",
    vet: "",
    reason: "",
    date: "",
    time: "",
    type: "",
  });

  // Refs for Flatpickr
  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);
  const datePickerRef = useRef(null);
  const timePickerRef = useRef(null);

  // Generate time slots based on working hours
  const generateTimeSlots = (workingHours) => {
    if (!workingHours || workingHours.length === 0) return [];

    const slots = [];
    workingHours.forEach(wh => {
      const startTime = wh.opening_time;
      const endTime = wh.closing_time;
      const dayKey = String(wh.day || '').toLowerCase();

      // Convert time strings to minutes for easier calculation
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);

      // Generate 30-minute slots
      for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
        const timeString = minutesToTime(minutes);
        slots.push({
          time: timeString,
          display: timeString,
          day: dayKey
        });
      }
    });

    return slots.sort((a, b) => a.time.localeCompare(b.time));
  };

  // Helper functions for time conversion
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Helper function to parse appointment_at datetime string and split into date and time
  const parseAppointmentAt = (appointmentAt) => {
    if (!appointmentAt) return { date: '', time: '' };

    try {

      // Parse the datetime string (format: "2025-09-25 13:00:00")
      const dateTime = new Date(appointmentAt);

      // Extract date in YYYY-MM-DD format
      const date = dateTime.toISOString().split('T')[0];

      // Extract time in HH:MM format - use getHours and getMinutes for more reliable parsing
      const hours = dateTime.getHours().toString().padStart(2, '0');
      const minutes = dateTime.getMinutes().toString().padStart(2, '0');
      const time = `${hours}:${minutes}`;


      return { date, time };
    } catch (error) {
      console.error('❌ Error parsing appointment_at:', error);
      return { date: '', time: '' };
    }
  };

  // Helper function to combine date and time into appointment_at format
  const combineDateTime = (date, time) => {
    if (!date || !time) return null;

    try {
      // Combine date and time into ISO format, then convert to the expected format
      const dateTime = new Date(`${date}T${time}:00`);
      return dateTime.toISOString().replace('T', ' ').substring(0, 19);
    } catch (error) {
      console.error('Error combining date and time:', error);
      return null;
    }
  };

  // When Pet Guardian changes, fetch their pets
  useEffect(() => {
    if (selectedPetOwner) {
      petAPI.getOtherPets(selectedPetOwner.id).then((res) => {
        setPetOwnerPets(res.data?.data || []);
      });
    } else {
      setPetOwnerPets([]);
    }
  }, [selectedPetOwner]);

  // Pet Guardian search with debouncing
  useEffect(() => {
    if (!petOwnerSearch.trim()) {
      setPetOwnerSearchResults([]);
      setIsSearchingPetOwners(false);
      return;
    }
    setIsSearchingPetOwners(true);
    const timeoutId = setTimeout(() => {
      clinicAPI.searchGuardians(petOwnerSearch)
        .then((response) => {
          setPetOwnerSearchResults(response.data?.data || []);
          setIsSearchingPetOwners(false);
        })
        .catch((error) => {
          console.error('Error searching Pet Guardians:', error);
          setPetOwnerSearchResults([]);
          setIsSearchingPetOwners(false);
        });
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [petOwnerSearch]);

  // Generate time slots when working hours change
  useEffect(() => {
    const slots = generateTimeSlots(workingHours);
    setAvailableTimeSlots(slots);
  }, [workingHours]);

  // Force refresh time slots when modal opens
  useEffect(() => {
    if (isOpen) {
      const slots = generateTimeSlots(workingHours);
      setAvailableTimeSlots(slots);
    }
  }, [isOpen, workingHours]);

  // Update time display when editOutpatient.time changes
  useEffect(() => {
  }, [editOutpatient.time]);

  // Filter time slots based on selected date and current time
  // - For future dates: show all available time slots
  // - For today: show only time slots that are 15+ minutes in the future
  const getAvailableTimeSlotsForDate = (selectedDate) => {
    if (!selectedDate) {
      return availableTimeSlots;
    }

    const selectedDay = new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const selectedDateObj = new Date(selectedDate);
    const today = new Date();

    // Check if selected date is today
    const isToday = selectedDateObj.toDateString() === today.toDateString();

    let filteredSlots = availableTimeSlots.filter(slot => slot.day === selectedDay);



    if (isToday) {
      // Filter out past time slots for today
      const currentHour = today.getHours();
      const currentMinute = today.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;

      const originalSlotsCount = filteredSlots.length;
      filteredSlots = filteredSlots.filter(slot => {
        const [slotHour, slotMinute] = slot.time.split(':').map(Number);
        const slotTimeInMinutes = slotHour * 60 + slotMinute;

        // Show time slots that are at least 15 minutes in the future (reduced from 30 to be less restrictive)
        const isFutureSlot = slotTimeInMinutes >= (currentTimeInMinutes + 15);


        return isFutureSlot;
      });

    }

    return filteredSlots;
  };

  // Initialize Flatpickr for date picker only
  useEffect(() => {
    if (isOpen && dateInputRef.current) {
      // Initialize date picker
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }

      // Create array of available days based on working hours
      const dayMap = {
        'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
        'thursday': 4, 'friday': 5, 'saturday': 6
      };
      const availableDays = workingHours
        .map(wh => {
          const dayKey = String(wh.day || '').toLowerCase();
          return dayMap[dayKey];
        })
        .filter((d) => d !== undefined);

      // Enable working hours restriction and disable past dates
      const disableFunction = workingHours.length > 0 ? [
        function (date) {
          // Disable past dates
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (date < today) {
            return true;
          }

          // Disable dates that are not in working hours
          const dayOfWeek = date.getDay();
          const isDisabled = !availableDays.includes(dayOfWeek);
          return isDisabled;
        }
      ] : [
        function (date) {
          // Disable past dates when no working hours are set
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isPastDate = date < today;
          return isPastDate;
        }
      ];

      datePickerRef.current = flatpickr(dateInputRef.current, {
        dateFormat: "Y-m-d",
        minDate: "today",
        disable: disableFunction,
        onChange: function (selectedDates, dateStr) {
          setEditOutpatient(prev => ({ ...prev, date: dateStr, time: "" })); // Reset time when date changes
          setShowTimeDropdown(false); // Close time dropdown when date changes
        }
      });

      // Set the date after Flatpickr is initialized - use a timeout to ensure it's set after initialization
      setTimeout(() => {
        if (editOutpatient.date && datePickerRef.current) {
          datePickerRef.current.setDate(editOutpatient.date);
        }
      }, 100);
    }

    return () => {
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }
    };
  }, [isOpen, workingHours, editOutpatient.date]);

  // Load edit data when modal opens
  useEffect(() => {
    if (isOpen && editData) {
      // Extract time from originalData if available, otherwise use the time field
      let extractedTime = editData.time || "";
      let extractedDate = editData.date || "";

      // If we have originalData with appointment_at, extract from there
      if (editData.originalData?.appointment_at) {
        const appointmentAt = editData.originalData.appointment_at;
        if (appointmentAt.includes('T')) {
          const [datePart, timePart] = appointmentAt.split('T');
          extractedDate = datePart;
          extractedTime = timePart ? timePart.substring(0, 5) : "";
        } else {
          // Use the new parseAppointmentAt function for better parsing
          const parsed = parseAppointmentAt(appointmentAt);
          extractedDate = extractedDate || parsed.date;
          extractedTime = extractedTime || parsed.time;
        }
      }

      // Additional fallback: if we still don't have date/time, try parsing from visit_date
      if (editData.originalData?.visit_date && (!extractedDate || !extractedTime)) {
        const parsed = parseAppointmentAt(editData.originalData.visit_date);
        extractedDate = extractedDate || parsed.date;
        extractedTime = extractedTime || parsed.time;
      }

      // Populate form with existing data


      setEditOutpatient({
        id: editData.id,
        pet_owner: editData.pet_owner || "",
        pet: editData.pet || "",
        vet: editData.vet || "",
        reason: editData.reason || "",
        date: extractedDate,
        time: extractedTime,
        type: editData.type || "",
      });

      // Set downpayment and store original value
      const originalDownpaymentValue = editData.downpayment || 0;
      setDownpayment(originalDownpaymentValue);
      setOriginalDownpayment(originalDownpaymentValue);

      // Set payment data
      const paymentNotesValue = editData.originalData?.payments_note || editData.payments_note || "";
      const paymentTypeValue = editData.originalData?.payment_type_id || editData.payment_type_id || null;



      setPaymentNotes(paymentNotesValue);
      setSelectedPaymentMethod(paymentTypeValue);

      // Set selected Pet Guardian if available
      if (editData.originalData?.pet_owner) {
        setSelectedPetOwner(editData.originalData.pet_owner);
        setPetOwnerSearch(editData.pet_owner);
      }

      // Set selected vet if available
      if (editData.originalData?.vet) {
        setSelectedVet(editData.originalData.vet);
        // Try multiple ways to get the vet name
        const vetName = editData.originalData.vet.user?.name ||
          editData.originalData.vet.name ||
          (editData.originalData.vet.first_name && editData.originalData.vet.last_name
            ? `${editData.originalData.vet.first_name} ${editData.originalData.vet.last_name}`.trim()
            : null) ||
          `Vet ${editData.originalData.vet.user_id || editData.originalData.vet.id || 'Unknown'}`;
        setVetSearch(vetName);
      }

      // Set selected pet if available
      if (editData.originalData?.pet) {
        setSelectedPet(editData.originalData.pet);
      }

      // Debug logging to see what data we're working with

    } else if (isOpen && !editData) {
      // Reset form if no edit data
      setEditOutpatient({
        id: null,
        pet_owner: "",
        pet: "",
        vet: "",
        reason: "",
        date: "",
        time: "",
        type: "",
      });
      setDownpayment(null);
      setOriginalDownpayment(null);
      setPaymentNotes("");
      setSelectedPaymentMethod(null);
      setPetOwnerSearch("");
      setSelectedPetOwner(null);
      setPetOwnerPets([]);
      setPetOwnerSearchResults([]);
      setShowPetOwnerDropdown(false);
      setIsSearchingPetOwners(false);
      setVetSearch("");
      setSelectedVet(null);
      setShowVetDropdown(false);
      setSelectedPet(null);
      setShowTimeDropdown(false);
    }
  }, [isOpen, editData]);


  const handleSaveClick = async () => {
    // Get clinic ID using multiple approaches
    const currentUser = authUtils.getCurrentUser();
    const clinicIdFromHook = getClinicId();
    const clinicId = clinicIdFromHook ||
      currentUser?.clinic?.id ||
      currentUser?.clinic_id;

    if (!clinicId) {
      showErrorToast(t("receptionOutpatients.admit.toasts.clinicMissing"));
      return;
    }

    // Validate required fields
    if (!selectedPetOwner) {
      showErrorToast(t("receptionOutpatients.admit.toasts.petGuardianRequired"));
      return;
    }
    if (!selectedPet) {
      showErrorToast(t("receptionOutpatients.admit.toasts.petRequired"));
      return;
    }
    if (!selectedVet) {
      showErrorToast(t("receptionOutpatients.admit.toasts.vetRequired"));
      return;
    }
    if (!editOutpatient.date) {
      showErrorToast(t("receptionAppointments.create.toasts.dateRequired"));
      return;
    }
    if (!editOutpatient.time) {
      showErrorToast(t("receptionAppointments.create.toasts.timeRequired"));
      return;
    }

    // Check if downpayment has changed
    const currentDownpayment = downpayment || 0;
    const hasDownpaymentChanged = currentDownpayment !== (originalDownpayment || 0);

    if (hasDownpaymentChanged) {
      // Show payment modal only if downpayment changed
      setShowPaymentModal(true);
    } else {
      // Proceed directly to save without payment modal
      await handleDirectSave();
    }
  };

  const handleDirectSave = async () => {
    try {
      // Get clinic ID using multiple approaches
      const currentUser = authUtils.getCurrentUser();
      const clinicIdFromHook = getClinicId();
      const clinicId = clinicIdFromHook ||
        currentUser?.clinic?.id ||
        currentUser?.clinic_id;

      if (!clinicId) {
        showErrorToast(t("receptionOutpatients.admit.toasts.clinicMissing"));
        return;
      }

      // Format visit_date as datetime string (YYYY-MM-DD HH:MM:SS)
      const visitDateTime = `${editOutpatient.date} ${editOutpatient.time}:00`;

      // Prepare API payload with required fields
      const apiPayload = {
        pet_owner_id: selectedPetOwner.id,
        pet_id: selectedPet.id,
        vet_id: selectedVet.user_id,
        visit_date: visitDateTime,
        clinic_id: clinicId,
        downpayment: downpayment || 0,
        reason: editOutpatient.reason,
        payment_type_id: selectedPaymentMethod,
        payments_note: paymentNotes
      };

      // Ensure token is available
      const token = localStorage.getItem('access_token');
      if (!token) {
        showErrorToast(t("receptionOutpatients.edit.toasts.authMissing"));
        return;
      }

      // Call the API to update the outpatient visit
      const response = await outpatientAPI.updateOutpatientVisit(editOutpatient.id, apiPayload);

      if (response.data) {
        showSuccessToast(t("receptionOutpatients.edit.toasts.saveSuccess"));

        // Return full API response data similar to EditInpatientModal
        const updatedRow = {
          id: editOutpatient.id,
          pet_owner_id: selectedPetOwner.id,
          pet_id: selectedPet.id,
          vet_id: selectedVet.user_id,
          reason: editOutpatient.reason,
          date: editOutpatient.date,
          time: editOutpatient.time,
          appointment_type_id: editOutpatient.type,
          downpayment: downpayment,
          payment_type_id: selectedPaymentMethod,
          payments_note: paymentNotes,
          // Display data for table
          pet_owner: selectedPetOwner
            ? selectedPetOwner.display_name ||
            selectedPetOwner.full_name ||
            `${selectedPetOwner.first_name} ${selectedPetOwner.last_name}` ||
            fallbackLabels.petGuardian
            : fallbackLabels.petGuardian,
          pet: selectedPet ? selectedPet.name : fallbackLabels.pet,
          vet: selectedVet
            ? getVetDisplayName(selectedVet)
            : fallbackLabels.vet,
          originalData: response.data
        };

        await onSave(updatedRow);
      }
    } catch (error) {
      console.error('Error updating outpatient visit:', error);
      showErrorToast(error.response?.data?.message || t("receptionOutpatients.edit.toasts.saveFailed"));
    }
  };

  const handlePaymentSave = async (paymentInfo) => {
    try {
      // Update payment state
      setSelectedPaymentMethod(paymentInfo.method);
      setPaymentNotes(paymentInfo.notes || "");
      setShowPaymentModal(false);

      // Get clinic ID using multiple approaches
      const currentUser = authUtils.getCurrentUser();
      const clinicIdFromHook = getClinicId();
      const clinicId = clinicIdFromHook ||
        currentUser?.clinic?.id ||
        currentUser?.clinic_id;

      if (!clinicId) {
        showErrorToast(t("receptionOutpatients.admit.toasts.clinicMissing"));
        return;
      }

      // Format visit_date as datetime string (YYYY-MM-DD HH:MM:SS)
      const visitDateTime = `${editOutpatient.date} ${editOutpatient.time}:00`;

      // Prepare API payload with required fields
      const apiPayload = {
        pet_owner_id: selectedPetOwner.id,
        pet_id: selectedPet.id,
        vet_id: selectedVet.user_id,
        visit_date: visitDateTime,
        clinic_id: clinicId,
        downpayment: downpayment || 0,
        reason: editOutpatient.reason,
        payment_type_id: paymentInfo.method,
        payments_note: paymentInfo.notes
      };

      // Ensure token is available
      const token = localStorage.getItem('access_token');
      if (!token) {
        showErrorToast(t("receptionOutpatients.edit.toasts.authMissing"));
        return;
      }

      // Call the API to update the outpatient visit
      const response = await outpatientAPI.updateOutpatientVisit(editOutpatient.id, apiPayload);

      if (response.data) {
        showSuccessToast(t("receptionOutpatients.edit.toasts.saveSuccess"));

        // Return full API response data similar to EditInpatientModal
        const updatedRow = {
          id: editOutpatient.id,
          pet_owner_id: selectedPetOwner.id,
          pet_id: selectedPet.id,
          vet_id: selectedVet.user_id,
          reason: editOutpatient.reason,
          date: editOutpatient.date,
          time: editOutpatient.time,
          appointment_type_id: editOutpatient.type,
          downpayment: downpayment,
          payment_type_id: paymentInfo.method,
          payments_note: paymentInfo.notes,
          // Display data for table
          pet_owner: selectedPetOwner
            ? selectedPetOwner.display_name ||
            selectedPetOwner.full_name ||
            `${selectedPetOwner.first_name} ${selectedPetOwner.last_name}` ||
            fallbackLabels.petGuardian
            : fallbackLabels.petGuardian,
          pet: selectedPet ? selectedPet.name : fallbackLabels.pet,
          vet: selectedVet
            ? getVetDisplayName(selectedVet)
            : fallbackLabels.vet,
          originalData: response.data
        };

        await onSave(updatedRow);
      }
    } catch (error) {
      console.error('Error updating outpatient visit:', error);
      showErrorToast(error.response?.data?.message || t("receptionOutpatients.edit.toasts.saveFailed"));
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="reception-modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(4px)'
      }}>
        <div
          className="reception-modal"
          dir={i18n.dir()}
          lang={i18n.language}
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
          <div className="reception-modal-header" style={{
            padding: '0 32px 16px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827'
            }}>
              ✏️ {t("receptionOutpatients.edit.title")}
            </h2>
            <button
              className="reception-modal-close"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '8px',
                borderRadius: '8px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6b7280';
              }}
            >
              ✕
            </button>
          </div>
          <form
            className="reception-modal-form"
            onSubmit={(e) => {
              e.preventDefault();
            }}
            style={{
              paddingLeft: '32px',
              paddingRight: '32px'
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  👤 {t("receptionOutpatients.admit.fields.petGuardian.label")}
                </span>
                <div style={{ position: 'relative' }}>
                  {/* Dropdown Button */}
                  <div
                    className="reception-modal-input"
                    onClick={() => setShowPetOwnerDropdown(!showPetOwnerDropdown)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'all 0.2s',
                      outline: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'white'
                    }}
                  >
                    <span style={{ color: selectedPetOwner ? '#111827' : '#9ca3af' }}>
                      {selectedPetOwner
                        ? selectedPetOwner.display_name ||
                        selectedPetOwner.full_name ||
                        `${selectedPetOwner.first_name} ${selectedPetOwner.last_name}` ||
                        fallbackLabels.petGuardian
                        : t("receptionOutpatients.admit.fields.petGuardian.placeholder")
                      }
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {showPetOwnerDropdown ? '▲' : '▼'}
                    </span>
                  </div>

                  {/* Dropdown Content */}
                  {showPetOwnerDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 10,
                      maxHeight: '300px',
                      overflow: 'hidden',
                      marginTop: '4px'
                    }}>
                      {/* Search Input Inside Dropdown */}
                      <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <input
                          type="text"
                          placeholder={t("receptionOutpatients.admit.fields.petGuardian.searchPlaceholder")}
                          value={petOwnerSearch}
                          onChange={(e) => setPetOwnerSearch(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Results List */}
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {isSearchingPetOwners ? (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280'
                          }}>
                            <div style={{
                              display: 'inline-block',
                              width: '20px',
                              height: '20px',
                              border: '2px solid #e5e7eb',
                              borderTop: '2px solid #3b82f6',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                            <div style={{ marginTop: '8px', fontSize: '14px' }}>
                              {t("receptionOutpatients.admit.search.searching")}
                            </div>
                          </div>
                        ) : petOwnerSearchResults.length > 0 ? (
                          petOwnerSearchResults.map((g) => (
                            <div
                              key={g.id}
                              style={{
                                cursor: "pointer",
                                padding: "12px 16px",
                                borderBottom: '1px solid #f3f4f6',
                                transition: 'background-color 0.2s'
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedPetOwner(g);
                                setPetOwnerSearch(g.display_name || g.full_name || `${g.first_name} ${g.last_name}`);
                                setShowPetOwnerDropdown(false);
                                setPetOwnerSearchResults([]);
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f9fafb';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: '500', color: '#111827' }}>
                                  {g.display_name ||
                                    g.full_name ||
                                    `${g.first_name} ${g.last_name}` ||
                                    fallbackLabels.petGuardian}
                                </div>
                                {g.last_completed_appointment_relation && (
                                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                    {t("receptionAppointments.create.fields.lastVisit.dropdown", {
                                      date: new Date(g.last_completed_appointment_relation.appointment_at).toLocaleDateString(locale),
                                      status: g.last_completed_appointment_relation.status,
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : petOwnerSearch.trim() ? (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionOutpatients.admit.fields.petGuardian.empty")}
                          </div>
                        ) : (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionOutpatients.admit.fields.petGuardian.hint")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  🐾 {t("receptionAppointments.create.fields.pet.label")}
                </span>
                <select
                  className="reception-modal-input"
                  value={selectedPet ? selectedPet.name : ""}
                  onChange={(e) => {
                    const selectedPetName = e.target.value;
                    const pet = petOwnerPets.find(p => p.name === selectedPetName);
                    setSelectedPet(pet || null);
                  }}
                  disabled={!selectedPetOwner || petOwnerPets.length === 0}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: !selectedPetOwner ? '#f9fafb' : 'white',
                    cursor: !selectedPetOwner ? 'not-allowed' : 'pointer'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">
                    {!selectedPetOwner
                      ? t("receptionAppointments.create.fields.pet.guardianHint")
                      : petOwnerPets.length === 0
                        ? t("receptionAppointments.create.fields.pet.empty")
                        : t("receptionAppointments.create.fields.pet.placeholder")
                    }
                  </option>
                  {petOwnerPets.map((p) => (
                    <option key={p.id} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  👨‍⚕️ {t("receptionAppointments.create.fields.vet.label")}
                </span>
                <div style={{ position: 'relative' }}>
                  {/* Dropdown Button */}
                  <div
                    className="reception-modal-input"
                    onClick={() => setShowVetDropdown(!showVetDropdown)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'all 0.2s',
                      outline: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'white'
                    }}
                  >
                    <span style={{ color: selectedVet ? '#111827' : '#9ca3af' }}>
                      {selectedVet
                        ? getVetDisplayName(selectedVet)
                        : t("receptionAppointments.create.fields.vet.placeholder")
                      }
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {showVetDropdown ? '▲' : '▼'}
                    </span>
                  </div>

                  {/* Dropdown Content */}
                  {showVetDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 10,
                      maxHeight: '300px',
                      overflow: 'hidden',
                      marginTop: '4px'
                    }}>
                      {/* Search Input Inside Dropdown */}
                      <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <input
                          type="text"
                          placeholder={t("receptionAppointments.create.fields.vet.searchPlaceholder")}
                          value={vetSearch}
                          onChange={(e) => setVetSearch(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Results List */}
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {vets.filter((v) => {
                          const displayName = getVetDisplayName(v);
                          return displayName && displayName.toLowerCase().includes(vetSearch.toLowerCase());
                        }).length > 0 ? (
                          vets
                            .filter((v) => {
                              const displayName = getVetDisplayName(v);
                              return displayName && displayName.toLowerCase().includes(vetSearch.toLowerCase());
                            })
                            .map((v) => {
                              const displayName = getVetDisplayName(v);
                              return (
                                <div
                                  key={v.user_id}
                                  style={{
                                    cursor: "pointer",
                                    padding: "12px 16px",
                                    borderBottom: '1px solid #f3f4f6',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedVet(v);
                                    setVetSearch(displayName);
                                    setShowVetDropdown(false);
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f9fafb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  {displayName}
                                </div>
                              );
                            })
                        ) : vetSearch.trim() ? (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionAppointments.create.fields.vet.empty")}
                          </div>
                        ) : (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionAppointments.create.fields.vet.hint")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  📝 {t("receptionOutpatients.admit.fields.reason.label")}
                </span>
                <input
                  type="text"
                  className="reception-modal-input"
                  value={editOutpatient.reason}
                  onChange={(e) =>
                    setEditOutpatient((a) => ({
                      ...a,
                      reason: e.target.value,
                    }))
                  }
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none'
                  }}
                  placeholder={t("receptionOutpatients.admit.fields.reason.placeholder")}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  💰 {t("receptionOutpatients.admit.fields.downpayment.label")}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="reception-modal-input"
                  value={downpayment}
                  onChange={(e) => setDownpayment(Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none'
                  }}
                  placeholder={t("receptionOutpatients.admit.fields.downpayment.placeholder")}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>

            </div>


            <div className="reception-modal-actions" style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'flex-end',
              paddingTop: '10px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                type="button"
                className="reception-action-btn"
                onClick={onClose}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = 'white';
                }}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="reception-action-btn"
                onClick={handleSaveClick}
                style={{
                  width: '40%',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2563eb';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#3b82f6';
                  e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                }}
              >
                💾 {t("receptionOutpatients.edit.buttons.save")}
              </button>
            </div>
          </form>
        </div>
        <SelectPaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSave={handlePaymentSave}
          title={t("receptionOutpatients.edit.payment.title")}
          paymentNotes={paymentNotes}
          paymentType={selectedPaymentMethod || ""}
          price={downpayment}
        />
      </div>
    </>
  );
};

export default EditOutpatientModal;
